import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePlaces } from '@/hooks/usePlaces';
import { useFavorites } from '@/hooks/useFavorites';
import { useInterests } from '@/hooks/useInterests';
import { PlaceCard } from '@/components/PlaceCard';
import { InterestCheck } from '@/components/InterestCheck';
import { INTEREST_CATALOG, placesForInterest } from '@/lib/interests';

export function FavoritesPage() {
  const { places, loading } = usePlaces();
  const { ids, isFavorite, toggle } = useFavorites();
  const { interestIds, hasInterest, toggleInterest } = useInterests();
  const saved = places.filter((p) => ids.includes(p.id));

  const sections = useMemo(
    () =>
      INTEREST_CATALOG.filter((interest) => interestIds.includes(interest.id)).map((interest) => ({
        interest,
        rows: placesForInterest(places, interest).slice(0, 12),
      })),
    [interestIds, places],
  );

  const hasAnything = interestIds.length > 0 || saved.length > 0;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Favorites</h1>
        <p className="page-subtitle">What I care about — stored on this device</p>
      </div>

      <div className="section-title">What I care about</div>
      <div className="chip-row" role="group" aria-label="What I care about">
        {INTEREST_CATALOG.map((interest) => (
          <InterestCheck
            key={interest.id}
            chip
            checked={hasInterest(interest.id)}
            label={interest.label}
            onToggle={() => toggleInterest(interest.id)}
          />
        ))}
      </div>

      {!hasAnything && (
        <div className="card empty-state">
          <p>Nothing checked yet. Pick Beaches, Food, or Walks — or heart a place from Places.</p>
        </div>
      )}

      {sections.map(({ interest, rows }) => {
        if (rows.length === 0 && !loading) return null;
        return (
          <section key={interest.id} style={{ marginTop: 16 }}>
            <h2 className="section-title">{interest.label}</h2>
            {rows.map((p) => (
              <PlaceCard key={p.id} place={p} isFavorite={isFavorite(p.id)} onToggleFavorite={toggle} />
            ))}
            <Link to={`/places?category=${interest.placeCategory}`} className="list-row">
              All {interest.label}
            </Link>
          </section>
        );
      })}

      {saved.length > 0 && (
        <section style={{ marginTop: 16 }}>
          <h2 className="section-title">Hearted places</h2>
          {saved.map((p) => (
            <PlaceCard key={p.id} place={p} isFavorite={isFavorite(p.id)} onToggleFavorite={toggle} />
          ))}
        </section>
      )}
    </div>
  );
}
